import React, { useEffect, useRef } from 'react';

import { secondsOnPage, trackEvent } from '../utils/track';
import CTASmall from './CTA-small';

const LOCATION = 'blog-mid';

/**
 * The mid-article CTA in blog posts, inserted at build time by
 * scripts/remark-mid-cta.js. Not meant to be written into a post by hand.
 *
 * Unlike every other CTA location this one points at the homepage rather than
 * `/register/`. Blog traffic is cold search traffic, and the three most common
 * register-abandonment answers all describe someone who reached the form too
 * early. The `<CTA />` at the foot of the post still asks for the trial, so a
 * post offers a ladder instead of the same ask twice.
 *
 * It reports its own visibility rather than leaning on the scroll-depth
 * thresholds, because the two are not the same measurement: scroll depth is
 * fixed at 25/50/75/100 while the CTA's position is a config value that may
 * become 33/66. Asking the CTA whether it was on screen keeps the answer true
 * wherever it ends up, and separates "never saw it" from "saw it, ignored it".
 */
const BlogMidCTA = () => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) return undefined;

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        // Once per pageview: this is reach, not a dwell counter.
        observer.disconnect();

        trackEvent('CTA visible', {
          location: LOCATION,
          page: window.location.pathname,
          seconds: secondsOnPage(),
        });
      },
      // Half of it on screen, so a CTA that only clipped the fold during a fast
      // scroll past doesn't count as seen.
      { threshold: 0.5 }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      <CTASmall
        track={LOCATION}
        text="Polypane has everything you need to build great sites"
        noCC
        link="/"
        buttonText="Learn about Polypane"
      />
    </div>
  );
};

export default BlogMidCTA;
